/**
 * Oriented source dimensions: ONE definition for every overlay, the crop
 * editor and the face boxes.
 *
 * Every coordinate Tank draws (crop boxes, face frame_bbox, recrops) lives in
 * ORIENTED source space: the original after its EXIF Orientation is applied.
 * exiftool's ImageWidth/ImageHeight are the STORED pixels, so a portrait shot
 * stored as 8640x5760 with "Rotate 90 CW" is 5760x8640 in the space the boxes
 * use. Normalising by the stored dims drew A1C04785's crop at 46% of the
 * width instead of 69% and shifted its face boxes (28-09-2026).
 *
 * Priority:
 *   1. pipeline.sati.original_dims (post-EXIF, authoritative)
 *   2. top-level original_dims (legacy merged meta)
 *   3. exiftool ImageWidth/ImageHeight, swapped for Orientation 5-8
 *      (exiftool print-converted text or the numeric -n form)
 *   4. caller fallbacks (face / photo dims), in the order given
 *   5. pipeline.sentinel.image_dims (last resort)
 */

// EXIF orientations 5-8 turn the frame a quarter. exiftool -json writes them
// as "Mirror horizontal and rotate 270 CW", "Rotate 90 CW",
// "Mirror horizontal and rotate 90 CW", "Rotate 270 CW"; -n gives 5-8.
const QUARTER_TURN_RE = /rotate\s*(90|270)|transpose|transverse/i;

export function exifIsQuarterTurn(orientation) {
  if (typeof orientation === 'number') return [5, 6, 7, 8].includes(orientation);
  const text = String(orientation ?? '').trim();
  if (/^[5-8]$/.test(text)) return true;
  return QUARTER_TURN_RE.test(text);
}

function pair(w, h) {
  const width = Number(w);
  const height = Number(h);
  return width > 0 && height > 0 ? { width, height } : null;
}

/**
 * @param {Object} metaDoc - merged photo metadata
 * @param {...Object} fallbacks - {width, height} candidates tried before sentinel image_dims
 * @returns {{width:number, height:number} | null}
 */
export function getOrientedSourceDims(metaDoc, ...fallbacks) {
  const sati = metaDoc?.pipeline?.sati?.original_dims;
  const fromSati = pair(sati?.width, sati?.height);
  if (fromSati) return fromSati;

  const legacy = metaDoc?.original_dims;
  const fromLegacy = pair(legacy?.width, legacy?.height);
  if (fromLegacy) return fromLegacy;

  const exif = metaDoc?.exiftool;
  const fromExif = pair(exif?.ImageWidth, exif?.ImageHeight);
  if (fromExif) {
    return exifIsQuarterTurn(exif?.Orientation)
      ? { width: fromExif.height, height: fromExif.width }
      : fromExif;
  }

  for (const fb of fallbacks) {
    const got = pair(fb?.width, fb?.height);
    if (got) return got;
  }

  const detect = metaDoc?.pipeline?.sentinel?.image_dims;
  return pair(detect?.width, detect?.height);
}

/**
 * Full-frame reference dims for the Edit page's face overlays ({w, h}).
 */
export function computeFullFrameDims(metaDoc, selectedFace, selectedPhoto) {
  const dims = getOrientedSourceDims(
    metaDoc,
    { width: selectedFace?.image_width, height: selectedFace?.image_height },
    { width: selectedPhoto?.image_width, height: selectedPhoto?.image_height },
    { width: selectedFace?.meta?.image_width, height: selectedFace?.meta?.image_height },
  );
  return { w: dims?.width, h: dims?.height };
}

/**
 * Scale from Sentinel detect space (image_dims) to oriented source pixels.
 * 1:1 when either side is unknown.
 */
export function detectToSourceScale(metaDoc, selectedPhoto) {
  const detect = metaDoc?.pipeline?.sentinel?.image_dims;
  const detW = Number(detect?.width);
  const detH = Number(detect?.height);
  const src = getOrientedSourceDims(metaDoc, {
    width: selectedPhoto?.image_width,
    height: selectedPhoto?.image_height,
  });
  if (!(detW > 0) || !(detH > 0) || !src) return { x: 1, y: 1 };
  return { x: src.width / detW, y: src.height / detH };
}
